import React from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PhoneIncoming, PhoneOff, Mic, MicOff, Check, X, Building2, Mail, Phone, User } from "lucide-react";

export function IncomingCallModal({ incoming, onAccept, onDecline }) {
  return (
    <div className="fixed inset-0 z-[100] bg-slate-900/60 flex items-center justify-center p-6" data-testid="incoming-call-modal">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-xl w-full max-w-md p-8">
        <div className="text-center">
          <div className="relative mx-auto h-20 w-20 rounded-full bg-teal-50 flex items-center justify-center">
            <span className="itp-ping absolute inset-0 rounded-full" />
            <PhoneIncoming className="h-7 w-7 text-teal-600" />
          </div>
          <div className="mt-4 text-xs uppercase tracking-widest text-slate-500">Incoming call</div>
        </div>
        <div className="mt-5 bg-slate-50 border border-slate-200 rounded-xl p-5 space-y-3" data-testid="caller-profile">
          <div className="flex items-center gap-3">
            <Building2 className="h-5 w-5 text-[#0a2540]" />
            <div>
              <div className="font-semibold text-[#0a2540]" data-testid="caller-company">
                {incoming.customer_company || incoming.customer_name}
              </div>
              <div className="text-xs text-slate-500">
                {incoming.customer_industry || "—"} · ID {incoming.customer_ref_id || "—"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 text-sm text-slate-600">
            <User className="h-4 w-4 text-slate-400" /> {incoming.customer_name}
          </div>
          <div className="flex items-center gap-3 text-sm text-slate-600">
            <Mail className="h-4 w-4 text-slate-400" /> {incoming.customer_email || "—"}
          </div>
          <div className="flex items-center gap-3 text-sm text-slate-600">
            <Phone className="h-4 w-4 text-slate-400" /> {incoming.customer_phone || "—"}
          </div>
          <div className="pt-2 border-t border-slate-200">
            <Badge variant="outline" className="bg-teal-50 text-teal-700 border-teal-200" data-testid="caller-language">
              Language: {incoming.language}
            </Badge>
          </div>
        </div>
        <div className="mt-6 flex items-center justify-center gap-4">
          <Button data-testid="decline-call-btn" onClick={onDecline}
            className="h-12 px-6 rounded-full bg-rose-600 hover:bg-rose-700 text-white">
            <X className="h-5 w-5 mr-2" /> Decline
          </Button>
          <Button data-testid="accept-call-btn" onClick={onAccept}
            className="h-12 px-6 rounded-full bg-emerald-600 hover:bg-emerald-700">
            <Check className="h-5 w-5 mr-2" /> Accept
          </Button>
        </div>
      </div>
    </div>
  );
}

export function ActiveCallModal({ call, elapsed, callStatus, muted, onToggleMute, onEnd }) {
  return (
    <div className="fixed inset-0 z-[100] bg-slate-900/60 flex items-center justify-center p-6" data-testid="active-call-modal">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-xl w-full max-w-md p-8">
        <div className="text-center">
          <div className="mx-auto h-20 w-20 rounded-full bg-emerald-100 flex items-center justify-center">
            <PhoneIncoming className="h-7 w-7 text-emerald-600" />
          </div>
          <div className="mt-3 text-xs uppercase tracking-widest text-emerald-600">In call</div>
          <div className="mt-2 text-3xl font-display font-light tabular-nums text-[#0a2540]" data-testid="interpreter-call-timer">
            {elapsed}
          </div>
          {callStatus && <div className="text-xs text-slate-400 mt-1">Connection: {callStatus}</div>}
        </div>
        <div className="mt-5 bg-slate-50 border border-slate-200 rounded-xl p-5 space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-slate-500">Company</span><span className="font-medium text-[#0a2540]">{call.customer_company || call.customer_name}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Contact</span><span className="font-medium">{call.customer_name}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Industry</span><span className="font-medium">{call.customer_industry || "—"}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Language</span><span className="font-medium">{call.language}</span></div>
        </div>
        <div className="mt-6 flex items-center justify-center gap-4">
          <Button variant="outline" onClick={onToggleMute} data-testid="interpreter-mute-btn" className="h-12 w-12 rounded-full p-0">
            {muted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
          </Button>
          <Button data-testid="interpreter-end-call-btn" onClick={onEnd}
            className="h-12 px-6 rounded-full bg-rose-600 hover:bg-rose-700 text-white">
            <PhoneOff className="h-5 w-5 mr-2" /> End call
          </Button>
        </div>
      </div>
    </div>
  );
}
